import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import MarketingLayout from "@/components/marketing/MarketingLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  ArrowRight, ArrowUpRight, Search, ChevronDown, ChevronUp,
  Wallet, ShieldCheck, PlugZap, FileText, HelpCircle, Rocket,
} from "lucide-react";

/**
 * /faq — Single-source buyer objections page.
 *
 * We ran the /inside-connexis FAQ dry after too many prospects asked the same
 * six questions on discovery calls. This page groups every recurring buyer
 * question into pillars (Pricing, Product, Data, Contracts, Integrations,
 * Compliance) and lets them ⌘F it in one shot.
 *
 * Content lifts + expands the seed set in InsideConnexis.jsx.
 */

const GROUPS = [
  {
    id: "pricing",
    title: "Pricing & billing",
    Icon: Wallet,
    body: "How our plans, wallets and margins work.",
    items: [
      {
        q: "Is the £99 / £149 / £249 plan a lead allowance?",
        a: "No. The plan is platform access — the API, dashboards, routing engine, compliance toolchain and support. Lead spend is separate and pay-as-you-go from your wallet.",
      },
      {
        q: "What's your margin on every lead?",
        a: "10%, baked into the sector price. If a mortgage lead costs you £38, the publisher receives £34.20 and Connexis keeps £3.80. No hidden uplift, no opaque routing fees.",
      },
      {
        q: "How does the wallet work?",
        a: "You top up your wallet upfront (from £5,000 to £50,000+ per top-up). We debit £X per accepted lead. Balance never expires. Auto-pauses at £0 so you never overspend by accident.",
      },
      {
        q: "Do you offer volume discounts?",
        a: "Yes — 100+ leads/order saves 5%, 250+ saves 10%, 500+ saves 15%. Beyond 1,000/month we run bespoke commercial terms; email billing@connex.is to open the conversation.",
      },
      {
        q: "Can I get an invoice instead of paying by card?",
        a: "Yes. Choose 'Bank transfer' at checkout and we'll email a PDF invoice immediately. Leads go live once funds have cleared — usually the same working day.",
      },
      {
        q: "Are AI Systems included in any plan?",
        a: "No, AI Systems (Voice, Chat, WhatsApp, Booker, Omnichannel, CRM sync) are separately ordered managed products with their own setup + monthly fees. They plug straight into your routing.",
      },
    ],
  },
  {
    id: "product",
    title: "Product & platform",
    Icon: Rocket,
    body: "How Connexis actually works day-to-day.",
    items: [
      {
        q: "Do you produce your own leads, or only route from partners?",
        a: "Both. We operate first-party consumer brands (Assura Cover, QuickFinance, SwiftCash, Solve Debts) that feed direct, exclusive enquiries into the network. They supplement — never displace — partner publisher volume.",
      },
      {
        q: "Are all leads exclusive?",
        a: "Yes. Every lead sold via Connexis is 100% exclusive — sold to exactly one buyer, never resold, never duplicated, never shopped around. Exclusivity is not an upsell; it is the product.",
      },
      {
        q: "How fast do leads arrive after a form-submit?",
        a: "Median 98ms from consumer form-submit to the first buyer-bid, and sub-second to your CRM under normal load. Every second we go over that shows up on the /status page in real time.",
      },
      {
        q: "How is the AI Voice agent built?",
        a: "End-to-end inside Connexis. Sub-300ms latency, FCA-aware compliance scripts, calendar-aware booking and full call recording for audit. You bring the script — we run the agent.",
      },
      {
        q: "Can I see real dashboard demos before signing up?",
        a: "Yes. Every screenshot on /inside-connexis is pulled live from the running platform. Book a strategy call to drive it yourself with our team.",
      },
    ],
  },
  {
    id: "integrations",
    title: "Integrations & delivery",
    Icon: PlugZap,
    body: "How leads land inside your stack.",
    items: [
      {
        q: "Which CRMs do you natively support?",
        a: "HubSpot, Salesforce, Pipedrive and Google Sheets via native connectors. Zapier for the long tail. Custom webhooks (HMAC-signed) for anything bespoke. See /integrations for the full map.",
      },
      {
        q: "What's the delivery format?",
        a: "JSON over HTTPS by default. Fields include lead identifiers, consent tokens, IP + user-agent, sector-specific extras, and a signed X-Connexis-Signature header. Node + Python verification snippets are in /docs.",
      },
      {
        q: "What happens if my endpoint is down?",
        a: "Stripe-style retry schedule — 30s → 2m → 10m → 1h → 4h, 5 attempts. Every attempt is logged; you can replay any failed delivery from your dashboard once the endpoint recovers.",
      },
      {
        q: "Do you support live phone transfers?",
        a: "Yes, on Live-transfer sectors. Our AI agent qualifies over SMS, warms the consumer, then bridges the call to your team. Timezone-aware, calendar-aware, fully recorded.",
      },
    ],
  },
  {
    id: "data",
    title: "Data & compliance",
    Icon: ShieldCheck,
    body: "GDPR, FCA, PECR — the tough questions.",
    items: [
      {
        q: "How do you handle consent?",
        a: "TrustedForm or an equivalent consent token, timestamp, IP, user-agent and referrer are captured at consumer form-submit and delivered with every lead. Audit-ready for FCA, FOS or ICO on demand.",
      },
      {
        q: "How do you handle suppression?",
        a: "Every accepted lead is checked against our TPS/CTPS suppression list plus your account-level suppression list before delivery. Unsubs propagate to all routing flows within minutes.",
      },
      {
        q: "Are you FCA-regulated?",
        a: "Connexis Group is FCA-aware (not FCA-authorised) — our routing engine, scripts and consent capture are designed around FCA CONC, ICOBS and MCOB. Buyers remain the accountable regulated firm.",
      },
      {
        q: "What if a consumer files a DSAR?",
        a: "Article 15 requests are routed to legal@connex.is, acknowledged within 24 hours and served within the 30-day statutory window. Free for the first request per consumer per 12 months.",
      },
      {
        q: "Where is data stored?",
        a: "MongoDB Atlas in EU-West (Ireland). Encrypted at rest (AES-256) and in transit (TLS 1.3). Daily backups with 30-day retention and quarterly restore drills. Full sub-processor list on /trust.",
      },
    ],
  },
  {
    id: "contracts",
    title: "Contracts & commitments",
    Icon: FileText,
    body: "Legal, MSA, DPA, notice periods.",
    items: [
      {
        q: "Is there a contract or minimum commitment?",
        a: "Platform plan is 30-day rolling. Wallet balance never expires. Cancel the plan, pause your campaigns, or withdraw your wallet — any time, no lock-in.",
      },
      {
        q: "Do you sign a DPA?",
        a: "Yes. A UK GDPR-aligned Data Processing Agreement is issued automatically at contract signing. Sub-processor schedule is available under NDA before purchase via legal@connex.is.",
      },
      {
        q: "Do you sign our vendor MSA?",
        a: "In most cases yes. Send it to legal@connex.is and expect turnaround within 1-2 business days. Our standard buyer terms are on /legal/terms-buyer if you'd prefer to counter-sign that.",
      },
      {
        q: "Do you accept vendor security questionnaires?",
        a: "Yes — SIG-Lite, VSAQ, custom Excel. Also happy to walk your infosec team through /trust or /trust-center on a call. Median response time is one business day.",
      },
    ],
  },
  {
    id: "getting-started",
    title: "Getting started",
    Icon: HelpCircle,
    body: "The five-minute path from zero to live.",
    items: [
      {
        q: "How quickly can I be live?",
        a: "Under 24 hours from signed order. Bank transfer takes as long as the transfer clears; card payment is instant. Once your CRM endpoint is verified, we go hot on the routing engine.",
      },
      {
        q: "Can I start with a small pilot?",
        a: "Yes — the smallest order size is 25 leads, so you can pressure-test a sector before committing meaningful spend. Volume discounts activate at 100+ leads/order.",
      },
      {
        q: "What if my leads don't convert?",
        a: "Every buyer gets a 48-hour dispute window per accepted lead. Verified issues (wrong number, duplicate, no-consent, out-of-sector) are refunded to your wallet with no argument.",
      },
      {
        q: "Do you offer managed acquisition or is it self-serve?",
        a: "Both. Buy-leads is self-serve out-of-the-box. If you want us to run the acquisition machinery around it — media, creative, BPO, CRM — that's what our /services stack is for.",
      },
    ],
  },
];

const ALL_ITEMS = GROUPS.flatMap((g) => g.items.map((it) => ({ ...it, group: g.title, groupId: g.id })));

/* ---------------------------------------------------------- */

export default function FAQ() {
  const [query, setQuery] = useState("");
  const [openId, setOpenId] = useState(null); // "groupId-index" or null

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return null;
    return ALL_ITEMS.filter((it) => it.q.toLowerCase().includes(q) || it.a.toLowerCase().includes(q));
  }, [query]);

  return (
    <MarketingLayout>
      <div className="bg-white" data-testid="faq-page">
        {/* ─── HERO ─── */}
        <section className="relative border-b border-slate-200 overflow-hidden bg-gradient-to-b from-white to-slate-50/40">
          <div className="absolute inset-0 dot-grid dot-grid-fade opacity-70" />
          <div className="absolute -top-32 -right-24 w-[620px] h-[620px] rounded-full bg-[#3d7bff]/[0.06] blur-3xl pointer-events-none" />
          <div className="relative mx-auto max-w-5xl px-4 sm:px-6 lg:px-8 pt-20 pb-14 lg:pt-24 lg:pb-16">
            <div className="inline-flex items-center gap-2 text-[10.5px] tracking-[0.22em] uppercase font-semibold text-[#0055FF]">
              <span className="size-1.5 rounded-full bg-emerald-500 animate-pulse" />
              Frequently asked questions
            </div>
            <h1 className="mt-3 text-4xl sm:text-5xl lg:text-6xl tracking-[-0.04em] font-semibold text-slate-900 leading-[1.03]" data-testid="faq-title">
              Every buyer objection — <br className="hidden lg:block" />
              <span className="bg-gradient-to-r from-slate-900 via-[#0055FF] to-[#3d7bff] bg-clip-text text-transparent">answered on one page.</span>
            </h1>
            <p className="mt-5 max-w-2xl text-[16.5px] text-slate-600 leading-relaxed">
              Pricing, product, data compliance, contracts, integrations, onboarding — the questions we hear on every
              discovery call, written out plainly so you don&apos;t have to book one to get the answer.
            </p>

            {/* Search */}
            <div className="mt-9 relative max-w-xl">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 size-4 text-slate-400" />
              <Input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search — try 'wallet', 'exclusive' or 'DPA'…"
                className="pl-11 h-12 rounded-full border-slate-300 bg-white shadow-soft focus-visible:ring-[#0055FF]/25"
                data-testid="faq-search"
              />
            </div>
            {query.trim() && (
              <p className="mt-3 text-[12.5px] text-slate-500" data-testid="faq-search-summary">
                {filtered.length === 0
                  ? <>No matches. <button onClick={() => setQuery("")} className="text-[#0055FF] hover:underline">Clear search</button> or <Link to="/contact" className="text-[#0055FF] hover:underline">ask us directly</Link>.</>
                  : <>{filtered.length} match{filtered.length === 1 ? "" : "es"}</>}
              </p>
            )}
          </div>
        </section>

        {/* ─── CONTENT ─── */}
        <section className="py-14 sm:py-20">
          <div className="mx-auto max-w-5xl px-4 sm:px-6 lg:px-8">
            {filtered ? (
              /* Search results */
              <div className="space-y-3" data-testid="faq-results">
                {filtered.map((it, i) => (
                  <FaqRow
                    key={`s-${i}`}
                    id={`search-${i}`}
                    item={it}
                    open={openId === `search-${i}`}
                    setOpen={setOpenId}
                    group={it.group}
                  />
                ))}
              </div>
            ) : (
              /* Grouped */
              <div className="space-y-16">
                {/* Sticky in-page nav */}
                <nav className="hidden lg:flex flex-wrap items-center gap-2 text-[12px]" data-testid="faq-nav">
                  {GROUPS.map((g) => (
                    <a
                      key={g.id}
                      href={`#${g.id}`}
                      className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 bg-white hover:border-[#0055FF]/40 hover:text-[#0055FF] px-3 py-1.5 text-slate-700 transition-colors"
                      data-testid={`faq-nav-${g.id}`}
                    >
                      <g.Icon className="size-3.5" /> {g.title}
                    </a>
                  ))}
                </nav>

                {GROUPS.map((g) => (
                  <div key={g.id} id={g.id} className="scroll-mt-24">
                    <div className="flex items-center gap-3">
                      <span className="inline-grid place-items-center size-10 rounded-lg bg-[#0055FF]/10 text-[#0055FF] ring-1 ring-[#0055FF]/15">
                        <g.Icon className="size-4.5" strokeWidth={1.7} />
                      </span>
                      <div>
                        <h2 className="text-2xl font-semibold text-slate-900 tracking-tight">{g.title}</h2>
                        <p className="text-[13px] text-slate-500 mt-0.5">{g.body}</p>
                      </div>
                    </div>
                    <div className="mt-6 space-y-3">
                      {g.items.map((it, i) => (
                        <FaqRow
                          key={`${g.id}-${i}`}
                          id={`${g.id}-${i}`}
                          item={it}
                          open={openId === `${g.id}-${i}`}
                          setOpen={setOpenId}
                        />
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </section>

        {/* ─── CTA ─── */}
        <section className="py-20 bg-slate-900 text-white border-t border-slate-800">
          <div className="mx-auto max-w-3xl px-6 text-center">
            <h2 className="text-3xl sm:text-4xl tracking-[-0.03em] font-medium leading-tight">Still have a question?</h2>
            <p className="mt-4 text-white/70 text-[15px] max-w-xl mx-auto leading-relaxed">
              Every buyer conversation improves this page. If you couldn&apos;t find your answer above, ask us directly and
              we&apos;ll add it here for the next person.
            </p>
            <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
              <Link to="/contact">
                <Button className="rounded-full px-5 h-11 bg-white text-slate-900 hover:bg-slate-100" data-testid="faq-cta-contact">
                  Ask us directly <ArrowUpRight className="size-4" />
                </Button>
              </Link>
              <Link to="/book-demo">
                <Button variant="outline" className="rounded-full px-5 h-11 border-white/25 bg-transparent hover:bg-white/10 text-white" data-testid="faq-cta-book">
                  Book a strategy call <ArrowRight className="size-4" />
                </Button>
              </Link>
            </div>
          </div>
        </section>
      </div>
    </MarketingLayout>
  );
}

/* ──────────── Row ──────────── */
function FaqRow({ id, item, open, setOpen, group }) {
  return (
    <div
      className={`rounded-xl border bg-white overflow-hidden transition-colors ${open ? "border-slate-300 shadow-soft" : "border-slate-200"}`}
      data-testid={`faq-row-${id}`}
    >
      <button
        onClick={() => setOpen(open ? null : id)}
        className="w-full px-5 sm:px-6 py-5 flex items-start justify-between gap-4 text-left transition-colors hover:bg-slate-50/60 focus-visible:bg-slate-50/60 focus:outline-none"
        aria-expanded={open}
        data-testid={`faq-toggle-${id}`}
      >
        <div className="min-w-0">
          {group && (
            <div className="text-[10px] font-semibold uppercase tracking-[0.18em] text-[#0055FF] mb-1.5">{group}</div>
          )}
          <div className="text-[15px] font-medium text-slate-900">{item.q}</div>
        </div>
        {open
          ? <ChevronUp className="size-4 text-slate-500 shrink-0 mt-1" />
          : <ChevronDown className="size-4 text-slate-500 shrink-0 mt-1" />}
      </button>
      {open && (
        <div className="px-5 sm:px-6 pt-1 pb-6 border-t border-slate-100">
          <p className="mt-4 text-[14px] text-slate-600 leading-relaxed">{item.a}</p>
        </div>
      )}
    </div>
  );
}
